import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import system from '../Constants/system';

const useProductCompatibility = (productId) => {
  const productCompatibilityRules = useSelector(state => state.OrderReducer.productCompatibilityRules);
  const vbProducts = useSelector(state => state.CustomerReducer.vbProducts);
  const order = useSelector(state => state.OrderReducer.order);

  const [compatibilityWarning, setCompatibilityWarning] = useState(null);
  const [warningAcknowledged, setWarningAcknowledged] = useState(false);

  const ownedProductIds = useMemo(() => {
    const products = new Set();
    
    vbProducts?.forEach(vbProduct => {
      if (vbProduct?.productNumber) {
        products.add(String(vbProduct.productNumber));
      }
    });
    
    return products;
  }, [vbProducts]);

  const cartProductIds = useMemo(() => {
    const products = new Set();
    
    order?.orderlines?.forEach(orderline => {
      let productIdToAdd = null;
      
      if (orderline.sourceSystem === system.Voss) {
        productIdToAdd = orderline.productId;
      } else if (orderline.sourceSystem === system.Vb) {
        productIdToAdd = orderline.productNumber;
      }
      
      if (productIdToAdd) {
        products.add(String(productIdToAdd));
      }
    });
    
    return products;
  }, [order?.orderlines]);

  const customerOwnedProducts = useMemo(() => {
    const combined = new Set([...ownedProductIds, ...cartProductIds]);
    return combined;
  }, [ownedProductIds, cartProductIds]);


  const checkCompatibility = useCallback((productIdToCheck) => {
    if (!productCompatibilityRules) {
      return null;
    }
    
    if (!productIdToCheck) {
      return null;
    }
    
    const normalizedProductId = String(productIdToCheck);
    const rulesForProduct = productCompatibilityRules[normalizedProductId];
    
    if (!rulesForProduct) {
      return null;
    }
    
    if (!Array.isArray(rulesForProduct)) {
      console.warn('[ProductCompatibility] Expected array of rules but got:', typeof rulesForProduct, rulesForProduct);
      return null;
    }
    
    for (const rule of rulesForProduct) {
      if (!rule.requiredOwnedProductIds || !Array.isArray(rule.requiredOwnedProductIds)) {
        console.warn('[ProductCompatibility] Invalid rule structure - missing or invalid requiredOwnedProductIds:', rule);
        continue;
      }
      
      if (rule.requiredOwnedProductIds.length === 0) {
        continue;
      }
      
      let ruleMatches = false;
      
      if (rule.matchType === 0) {
        ruleMatches = rule.requiredOwnedProductIds.every(reqId => 
          customerOwnedProducts.has(String(reqId))
        );
      } else if (rule.matchType === 1) {
        ruleMatches = rule.requiredOwnedProductIds.some(reqId => 
          customerOwnedProducts.has(String(reqId))
        );
      } else {
        console.warn(`[ProductCompatibility] Unknown matchType ${rule.matchType}`);
        continue;
      }
      
      if (ruleMatches) {
        const matchingProductIds = rule.requiredOwnedProductIds.filter(reqId =>
          customerOwnedProducts.has(String(reqId))
        );
        
        const isCartConflict = matchingProductIds.length > 0 && matchingProductIds.every(reqId => {
          const inCart = cartProductIds.has(String(reqId));
          const inOwned = ownedProductIds.has(String(reqId));
          return inCart && !inOwned;
        });
        
        const warningData = {
          message: rule.message,
          isCartConflict: isCartConflict
        };
        
        setCompatibilityWarning(warningData);
        return warningData;
      }
    }
    
    setCompatibilityWarning(null);
    return null;
  }, [productCompatibilityRules, customerOwnedProducts, ownedProductIds, cartProductIds]);

  useEffect(() => {
    if (productId) {
      checkCompatibility(productId);
    } else {
      setCompatibilityWarning(null);
      setWarningAcknowledged(false);
    }
  }, [productId, checkCompatibility]);

  const resetWarning = useCallback(() => {
    setCompatibilityWarning(null);
    setWarningAcknowledged(false);
  }, []);

  return {
    compatibilityWarning,
    warningAcknowledged,
    setWarningAcknowledged,
    checkCompatibility,
    resetWarning
  };
};

export default useProductCompatibility;
